import React, {useState, useRef, useEffect} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

export default function FilterPanel({
  site,
  onSiteChange,
  siteOptions,
  children,
  icon,
  disabled = false,
  // when true the dropdown button and menu will auto-size to their content instead of stretching to full width
  autoWidth = false,
  // maximum width in px to allow when autoWidth is true (optional)
  maxAutoWidth = null,
  // debounce delay in ms for resize measurements
  debounceDelay = 80,
  className,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const measureRef = useRef(null);
  const [measuredMinWidth, setMeasuredMinWidth] = useState(null);
  const debounceRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!autoWidth) return;
    const measure = () => {
      try {
        const container = measureRef.current;
        if (!container) return setMeasuredMinWidth(null);
        const buttons = Array.from(container.querySelectorAll('button'));
        if (buttons.length === 0) return setMeasuredMinWidth(null);
        const widths = buttons.map(b => b.getBoundingClientRect().width || b.offsetWidth || 0);
        const max = Math.max(...widths, 0);
        // add a tiny buffer for focus ring/rounding
        let w = Math.ceil(max) + 2;
        if (typeof maxAutoWidth === 'number') {
          w = Math.min(w, Math.max(0, Math.floor(maxAutoWidth)));
        }
        setMeasuredMinWidth(w);
      } catch (e) {
        setMeasuredMinWidth(null);
      }
    };

    const debouncedMeasure = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        measure();
        debounceRef.current = null;
      }, debounceDelay);
    };

    // measure immediately (useful when options change)
    measure();
    // debounce on resize
    window.addEventListener('resize', debouncedMeasure);
    return () => {
      window.removeEventListener('resize', debouncedMeasure);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [siteOptions, icon, autoWidth, maxAutoWidth, debounceDelay]);

  // Helper to compute dropdown position relative to viewport
  const recomputeDropdownPosition = () => {
    if (!rootRef.current) return;
    const button = rootRef.current.querySelector('button');
    if (!button) return;
    const rect = button.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom, left: rect.left, width: rect.width });
  };

  // Calculate dropdown position when opened (fixed-position portal → viewport coords)
  useEffect(() => {
    if (!open) return;
    recomputeDropdownPosition();
  }, [open]);

  // Reposition or close on scroll/resize to avoid floating dropdown on mobile/tablet
  useEffect(() => {
    if (!open) return;
    const handleResize = () => recomputeDropdownPosition();
    const handleScroll = () => {
      const vw = window.innerWidth;
      if (vw < 1024) {
        // On mobile/tablet, close on scroll to prevent orphaned menu
        setOpen(false);
      } else {
        // On desktop, keep it anchored to the trigger
        recomputeDropdownPosition();
      }
    };
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  // Close dropdown on outside click or Escape key
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e) => {
      if (!rootRef.current) return;
      // Check if click is outside the button and dropdown
      const isOutsideButton = !rootRef.current.contains(e.target);
      const isOutsideDropdown = !e.target.closest('[data-dropdown-portal]');
      if (isOutsideButton && isOutsideDropdown) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={("flex flex-col gap-3 sm:flex-row sm:items-center relative ") + (className || "") }>
      {/* Dropdown with icon */}
      <div className={"relative " + (autoWidth ? "w-auto" : "w-full sm:w-auto")}>
        <button
          disabled={disabled}
          onClick={() => setOpen(!open)}
          style={autoWidth && measuredMinWidth ? { minWidth: `${measuredMinWidth}px` } : !autoWidth ? { minWidth: '120px' } : undefined}
          className={`${autoWidth ? 'inline-flex' : 'w-full flex'} items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-gray-400 flex-shrink-0">{icon}</span>
            <span className="truncate text-xs sm:text-sm">
              {siteOptions.find(
                (option) =>
                  (typeof option === "string" ? option : option.key) === site
              )?.label || site}
            </span>
          </div>
          <ChevronDown
            className={`h-4 w-4 transition-transform ml-1 flex-shrink-0 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Dropdown menu */}
        {open && createPortal(
          <div
            data-dropdown-portal
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: measuredMinWidth ? `${measuredMinWidth}px` : `${dropdownPosition.width}px`,
              zIndex: 9999
            }}
            className={`mt-1 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg z-[9999]`}>
            {siteOptions.map((option) => {
              const value = typeof option === "string" ? option : option.key;
              const label = typeof option === "string" ? option : option.label;
              return (
                <div
                  key={value}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSiteChange(value);
                    setOpen(false);
                  }}
                  className={`cursor-pointer px-3 py-2.5 sm:py-2 text-xs sm:text-sm hover:bg-gray-100 touch-manipulation truncate ${
                    site === value ? "bg-gray-200" : ""
                  }`}
                >
                  {label}
                </div>
              );
            })}
          </div>,
          document.body
        )}
        {/* Hidden measurement container: renders a copy of each button (offscreen) to measure widths */}
        {autoWidth && (
          <div ref={measureRef} className="absolute left-[-9999px] top-[-9999px] pointer-events-none opacity-0">
            {siteOptions.map((option) => {
              const value = typeof option === "string" ? option : option.key;
              const label = typeof option === "string" ? option : option.label;
              return (
                <button key={value} className="inline-flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">{icon}</span>
                    <span>{label}</span>
                  </div>
                  <ChevronDown className="h-4 w-4 ml-1" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Additional filter children */}
      {children}
    </div>
  );
}
